import React, {
	useCallback,
	useEffect,
	useMemo,
	useState
} from 'react';
import PropTypes from 'prop-types';
import {
	Button,
	Divider,
	Form,
	Icon,
	Label,
	Message,
	Modal
} from 'semantic-ui-react';

import { stateOptions } from '../../../../../../services/constants';
import {
	validateEmail,
	validatePhone,
	validateZip
} from '../../../../../../services/validation';

import useForm from '../../../../../../hooks/useForm';

import {
	createAgencyLocation
	// updateAgencyLocation
} from '../../../../../../api/nora/agencies';

import { useAgencyState } from '../../../../../../contexts/AgenciesContext';
import { useUIAction } from '../../../../../../contexts/UIContext';

import EmailValidationInfoPopup from '../../../../../Form/EmailValidationInfoPopup';
import FormCheckbox from '../../../../../Form/FormCheckbox';
import FormErrors from '../../../../../Shared/FormErrors';
import FormInput from '../../../../../Form/FormInput';
import FormSelect from '../../../../../Form/FormSelect';
import FormTimeInput from '../../../../../Form/FormTimeInput';
import FormTextArea from '../../../../../Form/FormTextArea';
import PhoneValidationInfoPopup from '../../../../../Form/PhoneValidationInfoPopup';

import styles from '../../agencies.module.css';
import { formatTimeString } from '../../../../../../services/masks/time';

const LocationsModal = ( {
	onSave,
	open,
	closeModal,
	location
} ) => {
	const {
		agency,
		countyOptions,
		serviceOptionGroups,
		serviceOptions
	} = useAgencyState();
	const { setApiErrorAlert, setScreenLoader } = useUIAction();

	const [ forceValidation, setForceValidation ] = useState( false );

	const {
		formData,
		hasChanged: formDataHasChanged,
		updateField,
		setFormData
	} = useForm( {} );

	useEffect( () => {
		if ( location?.id ) {
			const services = location.services.reduce( ( acc, { items } ) => [ ...acc, ...items.map( ( { id } ) => id ) ], [] );
			const counties = location.counties.reduce( ( acc, { items } ) => [ ...acc, ...items.map( ( { id } ) => id ) ], [] );

			const hourData = location.hours.reduce( ( acc, day ) => {
				const label = day.label.slice( 0, 3 );

				return {
					...acc,
					[ `hoursFrom${ label }` ]: day.open,
					[ `hoursTo${ label }` ]: day.close,
					[ `isClosed${ label }` ]: day.isClosed,
					[ `hourNotes${ label }` ]: day.notes
				};
			}, {} );

			setFormData( {
				...location,
				services,
				counties,
				...hourData,
				phone: location.phone.number,
				ext: location.phone.extension,
				SMSEnabled: location.phone.isSMSEnabled,
				fax: location.fax.number
			} );
		} else {
			setFormData( {
				id: 0,
				hoursFromMon: '',
				hoursToMon: '',
				isClosedMon: false,
				hourNotesMon: '',
				hoursFromTue: '',
				hoursToTue: '',
				isClosedTue: false,
				hourNotesTue: '',
				hoursFromWed: '',
				hoursToWed: '',
				isClosedWed: false,
				hourNotesWed: '',
				hoursFromThu: '',
				hoursToThu: '',
				isClosedThu: false,
				hourNotesThu: '',
				hoursFromFri: '',
				hoursToFri: '',
				isClosedFri: false,
				hourNotesFri: '',
				hoursFromSat: '',
				hoursToSat: '',
				isClosedSat: false,
				hourNotesSat: '',
				hoursFromSun: '',
				hoursToSun: '',
				isClosedSun: false,
				hourNotesSun: ''
			} );
		}
	}, [
		location,
		setFormData
	] );

	const parseTimeString = useCallback( timeStr => {
		if ( timeStr?.length ) {
			let [ time, modifier ] = timeStr.split( ' ' );

			time = Number( formatTimeString( time ).replace( /^0+(?=\d:)/, '' )
				.replace( /[^0-9]/g, '' ) );

			time = time >= 1200 ? time - 1200 : time;

			let val = modifier === 'PM'
				? time + 1200
				: time;

			return val;
		}

		return 0;
	}, [] );

	const errors = useMemo( () => ( {
		name: [
			{
				error: !formData.name,
				message: 'Name is a required field.',
				fieldLabel: 'Name'
			}
		],
		address: [
			{
				error: !formData.address,
				message: 'Address is a required field.',
				fieldLabel: 'Address'
			}
		],
		city: [
			{
				error: !formData.city,
				message: 'City is a required field.',
				fieldLabel: 'City'
			}
		],
		state: [
			{
				error: !formData.state,
				message: 'State is a required field.',
				fieldLabel: 'State'
			}
		],
		zip: [
			{
				error: !formData.zip,
				message: 'Zip is a required field.',
				fieldLabel: 'Zip'
			},
			{
				error: Boolean( formData.zip ) && !validateZip( formData.zip ),
				message: 'Please enter a valid, 5-digit zip code.',
				fieldLabel: 'Zip'
			}
		],
		email: [
			{
				error: !formData.email,
				message: 'Email is a required field.',
				fieldLabel: 'Email'
			},
			{
				error: Boolean( formData.email ) && !validateEmail( formData.email ),
				message: <>Please enter a valid email address.&nbsp;<EmailValidationInfoPopup /></>,
				fieldLabel: 'Email'
			}
		],
		phone: [
			{
				error: !formData.phone,
				message: 'Phone is a required field.',
				fieldLabel: 'Phone'
			},
			{
				error: Boolean( formData.phone ) && !validatePhone( formData.phone ),
				message: <>Please enter a valid U.S. phone number.&nbsp;<PhoneValidationInfoPopup /></>,
				fieldLabel: 'Phone'
			}
		],
		fax: [
			{
				error: Boolean( formData.fax ) && !validatePhone( formData.fax ),
				message: <>Please enter a valid U.S. Phone number.&nbsp;<PhoneValidationInfoPopup /></>,
				fieldLabel: 'Fax'
			}
		],
		services: [
			{
				error: !formData.services?.length,
				message: 'Services is a required field.',
				fieldLabel: 'Services'
			}
		],
		counties: [
			{
				error: !formData.counties?.length,
				message: 'Counties is a required field.',
				fieldLabel: 'Counties'
			}
		],
		hoursToMon: [
			{
				error: !formData.isClosedMon && formData.hoursFromMon?.length
					&& parseTimeString( formData.hoursFromMon ) >= parseTimeString( formData.hoursToMon ),
				message: 'Closing hours must be greater than opening hours.',
				fieldLabel: 'Mon - To'
			}
		],
		hoursToTue: [
			{
				error: !formData.isClosedTue && formData.hoursFromTue?.length
					&& parseTimeString( formData.hoursFromTue ) >= parseTimeString( formData.hoursToTue ),
				message: 'Closing hours must be greater than opening hours.',
				fieldLabel: 'Tue - To'
			}
		],
		hoursToWed: [
			{
				error: !formData.isClosedWed && formData.hoursFromWed?.length
					&& parseTimeString( formData.hoursFromWed ) >= parseTimeString( formData.hoursToWed ),
				message: 'Closing hours must be greater than opening hours.',
				fieldLabel: 'Wed - To'
			}
		],
		hoursToThu: [
			{
				error: !formData.isClosedThu && formData.hoursFromThu?.length
					&& parseTimeString( formData.hoursFromThu ) >= parseTimeString( formData.hoursToThu ),
				message: 'Closing hours must be greater than opening hours.',
				fieldLabel: 'Thu - To'
			}
		],
		hoursToFri: [
			{
				error: !formData.isClosedFri && formData.hoursFromFri?.length
					&& parseTimeString( formData.hoursFromFri ) >= parseTimeString( formData.hoursToFri ),
				message: 'Closing hours must be greater than opening hours.',
				fieldLabel: 'Fri - To'
			}
		],
		hoursToSat: [
			{
				error: !formData.isClosedSat && formData.hoursFromSat?.length
					&& parseTimeString( formData.hoursFromSat ) >= parseTimeString( formData.hoursToSat ),
				message: 'Closing hours must be greater than opening hours.',
				fieldLabel: 'Sat - To'
			}
		],
		hoursToSun: [
			{
				error: !formData.isClosedSun && formData.hoursFromSun?.length
					&& parseTimeString( formData.hoursFromSun ) >= parseTimeString( formData.hoursToSun ),
				message: 'Closing hours must be greater than opening hours.',
				fieldLabel: 'Sun - To'
			}
		]
	} ), [
		formData.address,
		formData.city,
		formData.counties?.length,
		formData.email,
		formData.fax,
		formData.hoursFromFri,
		formData.hoursFromMon,
		formData.hoursFromSat,
		formData.hoursFromSun,
		formData.hoursFromThu,
		formData.hoursFromTue,
		formData.hoursFromWed,
		formData.hoursToFri,
		formData.hoursToMon,
		formData.hoursToSat,
		formData.hoursToSun,
		formData.hoursToThu,
		formData.hoursToTue,
		formData.hoursToWed,
		formData.isClosedFri,
		formData.isClosedMon,
		formData.isClosedSat,
		formData.isClosedSun,
		formData.isClosedThu,
		formData.isClosedTue,
		formData.isClosedWed,
		formData.name,
		formData.phone,
		formData.services?.length,
		formData.state,
		formData.zip,
		parseTimeString
	] );

	const validate = useCallback( () => Object.values( errors ).some( errs => errs.some( err => err.error ) ), [ errors ] );

	const mapFormErrors = useCallback(
		() => Object.values( errors ).map( errors => errors.map( error => error.error
			? <Message.Item key={ error.fieldLabel }>{ error.fieldLabel }</Message.Item>
			: null ) ),
		[ errors ]
	);

	const handleSave = useCallback( async () => {
		if ( validate() ) {
			if ( !forceValidation ) {
				setForceValidation( true );
			}
		} else {
			setForceValidation( false );

			// counties

			const {
				phone,
				ext,
				SMSEnabled,
				fax,
				hoursFromMon,
				hoursToMon,
				isClosedMon,
				hourNotesMon,
				hoursFromTue,
				hoursToTue,
				isClosedTue,
				hourNotesTue,
				hoursFromWed,
				hoursToWed,
				isClosedWed,
				hourNotesWed,
				hoursFromThu,
				hoursToThu,
				isClosedThu,
				hourNotesThu,
				hoursFromFri,
				hoursToFri,
				isClosedFri,
				hourNotesFri,
				hoursFromSat,
				hoursToSat,
				isClosedSat,
				hourNotesSat,
				hoursFromSun,
				hoursToSun,
				isClosedSun,
				hourNotesSun,
				services,
				counties,
				...rest
			} = formData;

			const data = {
				...rest,
				services: [ { items: ( services ?? [] ).map( id => ( { id } ) ) } ],
				counties: [ { items: ( counties ?? [] ).map( id => ( { id } ) ) } ],
				hours: [
					{
						label: 'Monday',
						isClosed: isClosedMon,
						open: hoursFromMon,
						close: hoursToMon,
						notes: hourNotesMon
					},
					{
						label: 'Tuesday',
						isClosed: isClosedTue,
						open: hoursFromTue,
						close: hoursToTue,
						notes: hourNotesTue
					},
					{
						label: 'Wednesday',
						isClosed: isClosedWed,
						open: hoursFromWed,
						close: hoursToWed,
						notes: hourNotesWed
					},
					{
						label: 'Thursday',
						isClosed: isClosedThu,
						open: hoursFromThu,
						close: hoursToThu,
						notes: hourNotesThu
					},
					{
						label: 'Friday',
						isClosed: isClosedFri,
						open: hoursFromFri,
						close: hoursToFri,
						notes: hourNotesFri
					},
					{
						label: 'Saturday',
						isClosed: isClosedSat,
						open: hoursFromSat,
						close: hoursToSat,
						notes: hourNotesSat
					},
					{
						label: 'Sunday',
						isClosed: isClosedSun,
						open: hoursFromSun,
						close: hoursToSun,
						notes: hourNotesSun
					}
				],
				phone: {
					number: phone,
					extension: ext,
					isSMSEnabled: SMSEnabled
				},
				fax: { number: fax }
			};

			try {
				setScreenLoader( 'One moment please' );

				if ( location.id ) {
					await createAgencyLocation( agency.id, data );
				} else {
					await createAgencyLocation( agency.id, data );
				}
			} catch ( error ) {
				setApiErrorAlert( error );
			}

			await onSave();

			setScreenLoader( '' );
			closeModal();
		}
	}, [
		agency.id,
		closeModal,
		location,
		forceValidation,
		formData,
		onSave,
		setApiErrorAlert,
		setScreenLoader,
		validate
	] );

	const handleCloseModal = useCallback( () => {
		closeModal();
	}, [ closeModal ] );

	const emailIcon = useMemo(
		() => !errors.email.some( error => error.error )
			? <Icon
				color="black"
				link
				name="mail"
				onClick={ () => console.log( 'email contact' ) }
			/>
			: null
		, [ errors.email ]
	);

	const countryCode = useMemo( () => <Label basic><span>+1&nbsp;</span></Label>, [] );

	const handleIsClosed = useCallback( ( { target } ) => {
		if ( target.value ) {
			const day = target.name.replace( 'isClosed', '' );

			console.log( day );

			updateField( { target: { name: `hoursFrom${ day }`, value: '' } } );
			updateField( { target: { name: `hoursTo${ day }`, value: '' } } );
		}

		updateField( { target } );
	}, [ updateField ] );

	return (
		<Modal open={ open } className={ styles.staffModal }>
			<Modal.Header>
				{ location?.id
					? 'Edit Location'
					: 'Add Location'
				}
			</Modal.Header>
			<Modal.Content scrolling>
				<div>
					<Form
						autoComplete="off"
						error={ forceValidation && validate() }
					>
						<input
							className="hidden-input"
							name="name"
						/>
						<Form.Group>
							<FormInput
								errors={ errors.name }
								fieldLabel="Name"
								forceValidation={ forceValidation }
								maxLength={ 255 }
								name="name"
								onChange={ updateField }
								required
								value={ formData.name || '' }
								width={ 8 }
							/>
						</Form.Group>

						<Form.Group>
							<FormInput
								errors={ errors.address}
								fieldLabel="Address"
								forceValidation={ forceValidation }
								name="address"
								onChange={ updateField }
								required
								value={ formData.address || '' }
								width={ 10 }
							/>
						</Form.Group>

						<Form.Group>
							<FormInput
								fieldLabel="Address Line 2"
								name="address2"
								onChange={ updateField }
								value={ formData
									.address2 || '' }
								width={ 10 }
							/>
						</Form.Group>

						<Form.Group>
							<FormInput
								errors={ errors.city }
								fieldLabel="City"
								forceValidation={ forceValidation }
								name="city"
								onChange={ updateField }
								required
								value={ formData.city || '' }
								width={ 6 }
							/>
							<FormSelect
								errors={ errors.state }
								fieldLabel="State"
								forceValidation={ forceValidation }
								maxLength={ 2 }
								name="state"
								onChange={ updateField }
								options={ stateOptions }
								required
								search
								value={ formData.state || '' }
								width={ 2 }
							/>
							<FormInput
								errors={ errors.zip }
								fieldLabel="Zip"
								forceValidation={ forceValidation }
								keyDown="integer"
								maxLength={ 5 }
								name="zip"
								onChange={ updateField }
								required
								value={ formData.zip || '' }
								width={ 2 }
							/>
						</Form.Group>

						<Divider
							hidden
							style={ { margin: '1.5rem 0' } }
						/>

						<Form.Group>
							<FormInput
								errors={ errors.email }
								fieldLabel="Email"
								forceValidation={ forceValidation }
								icon={ emailIcon }
								maxLength={ 254 }
								name="email"
								required
								value={ formData.email || '' }
								onChange={ updateField }
								width={ 8 }
							/>
						</Form.Group>

						<Form.Group>
							<FormInput
								errors={ errors.phone }
								fieldLabel="Phone"
								forceValidation={ forceValidation }
								keyDown="integer"
								inputLabel={ countryCode }
								mask="phone"
								maxLength={ 14 }
								name="phone"
								onChange={ updateField }
								required
								value={ formData.phone || '' }
								width={ 6 }
							/>
							<FormInput
								fieldLabel="Ext."
								forceValidation={ forceValidation }
								keyDown="integer"
								name="ext"
								onChange={ updateField }
								value={ formData.ext || '' }
								width={ 3 }
							/>
							<FormCheckbox
								checked={ formData.SMSEnabled }
								fieldLabel="SMS Enabled"
								name="SMSEnabled"
								onChange={ updateField }
								width={ 3 }
							/>
						</Form.Group>

						<Form.Group>
							<FormInput
								errors={ errors.fax }
								fieldLabel="Fax"
								forceValidation={ forceValidation }
								keyDown="integer"
								inputLabel={ countryCode }
								mask="phone"
								maxLength={ 14 }
								name="fax"
								onChange={ updateField }
								value={ formData.fax || '' }
								width={ 6 }
							/>
						</Form.Group>

						<Divider
							hidden
							style={ { margin: '1.5rem 0' } }
						/>

						<Form.Group>
							<FormSelect
								errors={ errors.services }
								fieldLabel="Services Provided"
								forceValidation={ forceValidation }
								multiple
								name="services"
								onChange={ updateField }
								optionGroups={serviceOptionGroups}
								options={ serviceOptions }
								required
								search
								value={ formData.services || [] }
								width={ 12 }
							/>
						</Form.Group>

						<Form.Group>
							<FormSelect
								errors={ errors.counties }
								fieldLabel="Counties Served"
								forceValidation={ forceValidation }
								multiple
								name="counties"
								onChange={ updateField }
								options={ countyOptions }
								required
								search
								value={ formData.counties || [] }
								width={ 12 }
							/>
						</Form.Group>

						<Form.Group>
							<div style={{ width: '100%' }}>
								<Divider hidden />
								<h4>Monday Hours</h4>
								<Divider/>
							</div>
						</Form.Group>

						<Form.Group>
							<FormTimeInput
								disabled={ formData.isClosedMon }
								fieldLabel="From"
								keyDown="integer"
								maxLength={ 5 }
								name="hoursFromMon"
								onChange={ updateField }
								value={ formData.hoursFromMon || '' }
								width={ 4 }
							/>
							<FormTimeInput
								disabled={ formData.isClosedMon }
								errors={ errors.hoursToMon }
								defaultAmOrPm="PM"
								fieldLabel="To"
								forceValidation={ forceValidation }
								keyDown="integer"
								maxLength={ 5 }
								name="hoursToMon"
								onChange={ updateField }
								value={ formData.hoursToMon || '' }
								width={ 4 }
							/>
							<FormCheckbox
								checked={ formData.isClosedMon }
								fieldLabel="Office Closed"
								name="isClosedMon"
								onChange={ handleIsClosed }
								width={ 3 }
							/>
						</Form.Group>

						<Form.Group>
							<FormTextArea
								fieldLabel="Notes"
								name="hourNotesMon"
								onChange={ updateField }
								rows={ 6 }
								value={ formData.hourNotesMon || '' }
								width={ 16 }
							/>
						</Form.Group>

						{/* Tuesday */}
						<Form.Group>
							<div style={{ width: '100%' }}>
								<Divider hidden />
								<h4>Tuesday Hours</h4>
								<Divider/>
							</div>
						</Form.Group>

						<Form.Group>
							<FormTimeInput
								disabled={ formData.isClosedTue }
								fieldLabel="From"
								keyDown="integer"
								maxLength={ 5 }
								name="hoursFromTue"
								onChange={ updateField }
								value={ formData.hoursFromTue || '' }
								width={ 4 }
							/>
							<FormTimeInput
								disabled={ formData.isClosedTue }
								errors={ errors.hoursToTue }
								defaultAmOrPm="PM"
								fieldLabel="To"
								forceValidation={ forceValidation }
								keyDown="integer"
								maxLength={ 5 }
								name="hoursToTue"
								onChange={ updateField }
								value={ formData.hoursToTue || '' }
								width={ 4 }
							/>
							<FormCheckbox
								checked={ formData.isClosedTue }
								fieldLabel="Office Closed"
								name="isClosedTue"
								onChange={ handleIsClosed }
								width={ 3 }
							/>
						</Form.Group>

						<Form.Group>
							<FormTextArea
								fieldLabel="Notes"
								name="hourNotesTue"
								onChange={ updateField }
								rows={ 6 }
								value={ formData.hourNotesTue || '' }
								width={ 16 }
							/>
						</Form.Group>

						{/* Wednesday */}
						<Form.Group>
							<div style={{ width: '100%' }}>
								<Divider hidden />
								<h4>Wednesday Hours</h4>
								<Divider/>
							</div>
						</Form.Group>

						<Form.Group>
							<FormTimeInput
								disabled={ formData.isClosedWed }
								fieldLabel="From"
								keyDown="integer"
								maxLength={ 5 }
								name="hoursFromWed"
								onChange={ updateField }
								value={ formData.hoursFromWed || '' }
								width={ 4 }
							/>
							<FormTimeInput
								disabled={ formData.isClosedWed }
								errors={ errors.hoursToWed }
								defaultAmOrPm="PM"
								fieldLabel="To"
								forceValidation={ forceValidation }
								keyDown="integer"
								maxLength={ 5 }
								name="hoursToWed"
								onChange={ updateField }
								value={ formData.hoursToWed || '' }
								width={ 4 }
							/>
							<FormCheckbox
								checked={ formData.isClosedWed }
								fieldLabel="Office Closed"
								name="isClosedWed"
								onChange={ handleIsClosed }
								width={ 3 }
							/>
						</Form.Group>

						<Form.Group>
							<FormTextArea
								fieldLabel="Notes"
								name="hourNotesWed"
								onChange={ updateField }
								rows={ 6 }
								value={ formData.hourNotesWed || '' }
								width={ 16 }
							/>
						</Form.Group>

						{/* Thursday */}
						<Form.Group>
							<div style={{ width: '100%' }}>
								<Divider hidden />
								<h4>Thursday Hours</h4>
								<Divider/>
							</div>
						</Form.Group>

						<Form.Group>
							<FormTimeInput
								disabled={ formData.isClosedThu }
								fieldLabel="From"
								keyDown="integer"
								maxLength={ 5 }
								name="hoursFromThu"
								onChange={ updateField }
								value={ formData.hoursFromThu || '' }
								width={ 4 }
							/>
							<FormTimeInput
								disabled={ formData.isClosedThu }
								errors={ errors.hoursToThu }
								defaultAmOrPm="PM"
								fieldLabel="To"
								forceValidation={ forceValidation }
								keyDown="integer"
								maxLength={ 5 }
								name="hoursToThu"
								onChange={ updateField }
								value={ formData.hoursToThu || '' }
								width={ 4 }
							/>
							<FormCheckbox
								checked={ formData.isClosedThu }
								fieldLabel="Office Closed"
								name="isClosedThu"
								onChange={ handleIsClosed }
								width={ 3 }
							/>
						</Form.Group>

						<Form.Group>
							<FormTextArea
								fieldLabel="Notes"
								name="hourNotesThu"
								onChange={ updateField }
								rows={ 6 }
								value={ formData.hourNotesThu || '' }
								width={ 16 }
							/>
						</Form.Group>

						{/* Friday */}
						<Form.Group>
							<div style={{ width: '100%' }}>
								<Divider hidden />
								<h4>Friday Hours</h4>
								<Divider/>
							</div>
						</Form.Group>

						<Form.Group>
							<FormTimeInput
								disabled={ formData.isClosedFri }
								fieldLabel="From"
								keyDown="integer"
								maxLength={ 5 }
								name="hoursFromFri"
								onChange={ updateField }
								value={ formData.hoursFromFri || '' }
								width={ 4 }
							/>
							<FormTimeInput
								disabled={ formData.isClosedFri }
								errors={ errors.hoursToFri }
								defaultAmOrPm="PM"
								fieldLabel="To"
								forceValidation={ forceValidation }
								keyDown="integer"
								maxLength={ 5 }
								name="hoursToFri"
								onChange={ updateField }
								value={ formData.hoursToFri || '' }
								width={ 4 }
							/>
							<FormCheckbox
								checked={ formData.isClosedFri }
								fieldLabel="Office Closed"
								name="isClosedFri"
								onChange={ handleIsClosed }
								width={ 3 }
							/>
						</Form.Group>

						<Form.Group>
							<FormTextArea
								fieldLabel="Notes"
								name="hourNotesFri"
								onChange={ updateField }
								rows={ 6 }
								value={ formData.hourNotesFri || '' }
								width={ 16 }
							/>
						</Form.Group>

						{/* Saturday */}
						<Form.Group>
							<div style={{ width: '100%' }}>
								<Divider hidden />
								<h4>Saturday Hours</h4>
								<Divider/>
							</div>
						</Form.Group>

						<Form.Group>
							<FormTimeInput
								disabled={ formData.isClosedSat }
								fieldLabel="From"
								keyDown="integer"
								maxLength={ 5 }
								name="hoursFromSat"
								onChange={ updateField }
								value={ formData.hoursFromSat || '' }
								width={ 4 }
							/>
							<FormTimeInput
								disabled={ formData.isClosedSat }
								errors={ errors.hoursToSat }
								defaultAmOrPm="PM"
								fieldLabel="To"
								forceValidation={ forceValidation }
								keyDown="integer"
								maxLength={ 5 }
								name="hoursToSat"
								onChange={ updateField }
								value={ formData.hoursToSat || '' }
								width={ 4 }
							/>
							<FormCheckbox
								checked={ formData.isClosedSat }
								fieldLabel="Office Closed"
								name="isClosedSat"
								onChange={ handleIsClosed }
								width={ 3 }
							/>
						</Form.Group>

						<Form.Group>
							<FormTextArea
								fieldLabel="Notes"
								name="hourNotesSat"
								onChange={ updateField }
								rows={ 6 }
								value={ formData.hourNotesSat || '' }
								width={ 16 }
							/>
						</Form.Group>

						{/* Sunday */}
						<Form.Group>
							<div style={{ width: '100%' }}>
								<Divider hidden />
								<h4>Sunday Hours</h4>
								<Divider/>
							</div>
						</Form.Group>

						<Form.Group>
							<FormTimeInput
								disabled={ formData.isClosedSun }
								fieldLabel="From"
								keyDown="integer"
								maxLength={ 5 }
								name="hoursFromSun"
								onChange={ updateField }
								value={ formData.hoursFromSun || '' }
								width={ 4 }
							/>
							<FormTimeInput
								disabled={ formData.isClosedSun }
								errors={ errors.hoursToSun }
								defaultAmOrPm="PM"
								fieldLabel="To"
								forceValidation={ forceValidation }
								keyDown="integer"
								maxLength={ 5 }
								name="hoursToSun"
								onChange={ updateField }
								value={ formData.hoursToSun || '' }
								width={ 4 }
							/>
							<FormCheckbox
								checked={ formData.isClosedSun }
								fieldLabel="Office Closed"
								name="isClosedSun"
								onChange={ handleIsClosed }
								width={ 3 }
							/>
						</Form.Group>

						<Form.Group>
							<FormTextArea
								fieldLabel="Notes"
								name="hourNotesSun"
								onChange={ updateField }
								rows={ 6 }
								value={ formData.hourNotesSun || '' }
								width={ 16 }
							/>
						</Form.Group>
						<FormErrors mapFormErrors={ mapFormErrors } />
					</Form>
				</div>
			</Modal.Content>
			<Modal.Actions>
				<Button
					content="Save"
					disabled={ !formDataHasChanged }
					onClick={ handleSave }
				/>
				<Button
					content="Cancel"
					onClick={ handleCloseModal }
				/>
			</Modal.Actions>
		</Modal>
	);
};

LocationsModal.propTypes = {
	location: PropTypes.object,
	onSave: PropTypes.func.isRequired,
	open: PropTypes.bool,
	closeModal: PropTypes.func.isRequired
};

export default LocationsModal;
